import React from "react";
import { Info } from "lucide-react";
import { Header } from "@/components/jackstone/Header";
import { SectionCard } from "@/components/jackstone/SectionCard";
import { useSettings } from "@/hooks/useSettings";
import type { UnitSystem } from "@/lib/units";

// What each system shows, for the reference table under the toggles.
const UNIT_TABLE: [string, string, string][] = [
  ["Temperature", "°C", "°F"],
  ["Temperature difference", "K", "°F"],
  ["Density", "kg/m³", "lb/ft³"],
  ["Dimensions", "mm", "in (nearest ⅛)"],
  ["Weight", "kg", "lb"],
  ["Production per shift", "t", "US ton"],
  ["Mass flow / capacity", "kg/hr", "lb/hr"],
  ["Cooling load", "kW", "BTU/hr"],
  ["Volume flow", "L/hr", "gal/hr (US)"],
  ["Volume", "L", "gal (US)"],
  ["Pressure", "bar", "psi"],
];

/** Two-position SI / US segmented switch. */
function UnitSwitch({
  value, onChange, testId, label,
}: { value: UnitSystem; onChange: (v: UnitSystem) => void; testId: string; label: string }) {
  const opt = (sys: UnitSystem, text: string) => {
    const active = value === sys;
    return (
      <button
        type="button"
        role="radio"
        aria-checked={active}
        data-testid={`${testId}-${sys.toLowerCase()}`}
        onClick={() => onChange(sys)}
        className={`h-8 min-w-[92px] rounded-md px-3 text-[12px] font-semibold transition-colors cursor-pointer ${
          active
            ? "bg-[var(--js-blue)] text-white shadow-sm"
            : "text-[var(--js-text-2)] hover:text-[var(--js-navy)]"
        }`}
      >
        {text}
      </button>
    );
  };
  return (
    <div role="radiogroup" aria-label={label} data-testid={testId}
      className="inline-flex shrink-0 gap-1 rounded-lg border border-[var(--js-border)] bg-[var(--js-page)] p-1">
      {opt("SI", "SI (metric)")}
      {opt("US", "US")}
    </div>
  );
}

function SettingRow({
  title, description, children,
}: { title: string; description: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="text-[13px] font-semibold text-[var(--js-navy)]">{title}</p>
        <p className="mt-0.5 text-[12px] leading-relaxed text-[var(--js-text-2)]">{description}</p>
      </div>
      {children}
    </div>
  );
}

export default function SettingsPage() {
  const { uiUnits, pdfUnits, update } = useSettings();

  return (
    <div className="min-h-screen calc-grid-bg">
      <Header status={{ tone: "ready", label: "Ready" }} activePage="settings" />

      <main className="mx-auto max-w-[760px] px-4 sm:px-6 py-5">
        <SectionCard title="Units" testId="panel-settings-units">
          <div className="divide-y divide-[var(--js-border)] px-1">
            <SettingRow
              title="Interface units"
              description="Units shown and typed on the Calculator and Production Gantt pages."
            >
              <UnitSwitch value={uiUnits} onChange={(v) => update({ uiUnits: v })}
                testId="ui-units-switch" label="Interface units" />
            </SettingRow>
            <SettingRow
              title="PDF units"
              description="Units used in the exported Calculator Print Out, Throughput Schedule and Combined Report."
            >
              <UnitSwitch value={pdfUnits} onChange={(v) => update({ pdfUnits: v })}
                testId="pdf-units-switch" label="PDF units" />
            </SettingRow>
          </div>

          <div className="mt-2 rounded-md bg-[rgba(57,103,143,0.07)] px-3 py-2.5">
            <div className="flex items-start gap-2">
              <Info className="mt-[2px] h-3.5 w-3.5 shrink-0 text-[var(--js-blue)]" aria-hidden="true" />
              <p className="text-[11.5px] font-medium leading-relaxed text-[var(--js-text-2)]">
                Display only. Calculations, saved project files and the model code always stay in SI,
                so switching back and forth never changes a result.
              </p>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Unit reference" testId="panel-settings-unit-table">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-left text-[var(--js-text-2)]">
                <th className="py-1.5 pl-1 font-semibold">Quantity</th>
                <th className="py-1.5 font-semibold">SI</th>
                <th className="py-1.5 font-semibold">US</th>
              </tr>
            </thead>
            <tbody>
              {UNIT_TABLE.map(([q, si, us]) => (
                <tr key={q} className="border-t border-[var(--js-border)]">
                  <td className="py-1.5 pl-1 font-medium text-[var(--js-text)]">{q}</td>
                  <td className="py-1.5 font-semibold text-[var(--js-navy)]">{si}</td>
                  <td className="py-1.5 font-semibold text-[var(--js-navy)]">{us}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
      </main>
    </div>
  );
}
